import type { SliderProps } from "@carbon6/c6-frontend-design";
import { numericParamPolicy, type EvalParamSpec } from "./evalParamSpecs";

// 숫자 spec 을 슬라이더 범위로 바꾼다. 파라미터별 정책은 metadata 경계가 소유한다. 그 경계가
// 정책으로 정수 여부도 정한다.

export interface ParamSliderShape {
  min: number;
  max: number;
  step: NonNullable<SliderProps["step"]>;
}

// 슬라이더를 표시할 수 없으면 `null` 을 반환한다. 범위가 불완전하거나, 정책이 채운 뒤 범위가
// 뒤집힌 경우다. 정책은 metadata 가 비워 둔 끝만 채운다. 채운 끝은 항상 이미지가 선언한 끝의
// 반대쪽이다. 그래서 두 끝은 metadata 가 허용하는 범위 안에 있다.
export function resolveParamSlider(spec: EvalParamSpec): ParamSliderShape | null {
  const policy = numericParamPolicy(spec.key);
  const step = policy?.step ?? (spec.type === "int" ? 1 : "any");
  const min = spec.min ?? policy?.min;
  const max = spec.max ?? policy?.max;
  if (min === undefined || max === undefined || min >= max) {
    return null;
  }
  // 네이티브 range input 은 `min + n*step` 에 맞춘다. 범위가 step 격자에서 시작하지 않으면 모든
  // 정지점이 격자에서 벗어난다. 예: 이미지 `min` 1, step 50 이면 1, 51, 101 … 951 이 되어 1000 에
  // 닿지 못한다. 그래서 두 끝을 안쪽 격자로 당긴다. 정수 step 에만 적용한다. 실수 step 으로 나누면
  // 0.30000000000000004 같은 값이 다시 생긴다. c6 `Slider` 가 네이티브 input 을 소유하고 같은
  // `min + n*step` 으로 `marks` 를 만든다. 그래서 이 정렬은 c6 `normalizeBounds` 가 step 을 받게
  // 되면 그쪽에 속한다. 그때 여기서 제거한다.
  if (typeof step === "number" && Number.isInteger(step)) {
    const gridMin = Math.ceil(min / step) * step;
    const gridMax = Math.floor(max / step) * step;
    // 정지점 두 개를 둘 수 없을 만큼 좁으면 슬라이더를 비우지 않고 선언한 범위를 유지한다.
    if (gridMin < gridMax) {
      return { min: gridMin, max: gridMax, step };
    }
  }
  return { min, max, step };
}
